import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import {
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore'
import {
  auth,
  adminAuth,
  db,
  userRef,
  usersCollectionRef,
} from '../services/firebase'
import { UserViewModel, RoleType } from '../pages/ViewModel'

/**
 * @summary Signs in a user via their email and password.
 * @param {string} email The user's email address.
 * @param {string} password The user's password.
 * @returns Promise
 */
export const loginUser = async (email: string, password: string) => {
  await signInWithEmailAndPassword(auth, email, password)
}

/**
 * @summary Signs out the currently authenticated user.
 * @returns Promise
 */
export const logoutUser = async () => {
  return signOut(auth)
}

/**
 * @summary Returns the details of a user
 * @param {string} userID The ID of the user to be retrieved
 * @returns User data
 */
export async function getUserDetails(userID: string) {
  const docRef = userRef(userID)
  const docSnap = await getDoc(docRef)

  if (docSnap.exists()) {
    const {
      name,
      phone,
      email,
      role: { type, regions_accessed },
    } = docSnap.data()
    return {
      name,
      phone,
      email,
      role: {
        type: type,
        regions_accessed: regions_accessed ?? [],
      },
    } as UserViewModel
  }

  return null
}

/**
 * @summary Creates a user with their email and password, and saves extra details to a firebase doc
 * @returns uid
 */
export async function createUser(
  name: string,
  phone: string,
  email: string,
  password: string,
  roleType: RoleType,
  regions_accessed: string[]
) {
  if (name === null || phone === null) {
    throw Error('Creating User: Missing required user data')
  }

  const userCredential = await createUserWithEmailAndPassword(
    adminAuth,
    email,
    password
  )

  const batch = writeBatch(db)

  batch.set(userRef(userCredential.user.uid), {
    name,
    phone,
    email,
    role: {
      type: roleType,
      regions_accessed: regions_accessed ?? [],
    },
  })

  await batch.commit()
}

export async function getAllUsers(): Promise<UserViewModel[]> {
  const usersData: UserViewModel[] = []

  const userDocsSnap = await getDocs(usersCollectionRef)
  userDocsSnap.forEach((_user) => {
    const { name, email, phone, role } = _user.data()
    usersData.push({
      id: _user.id,
      name,
      email,
      phone,
      role: role,
    })
  })

  return usersData
}

export async function updateUser(
  id: string,
  name: string,
  email: string,
  phone: string,
  roleType: RoleType,
  regions_accessed: string[]
) {
  await updateDoc(userRef(id), {
    name,
    phone,
    role: {
      type: roleType,
      regions_accessed: regions_accessed,
    },
  })
}

export async function removeUser(user: any) {
  // if (!user && !user.id && !user.email) return;

  // // Sign-in with admin auth
  // const userCredential = await signInWithEmailAndPassword(
  //   adminAuth,
  //   user.email,
  //   user.password
  // );
  // const deletingUser = userCredential.user;
  // // Get the user with admin auth
  // await deleteUser(deletingUser);
  await deleteDoc(userRef(user.id))
}

/**
 * @summary Return staffs
 * @returns uid
 */
export async function getStaffs() {
  const q = query(usersCollectionRef, where('role.type', '==', 'staff'))

  const staffsData = []

  const memberDocsSnapshot = await getDocs(q)
  memberDocsSnapshot.forEach((_staff) => {
    staffsData.push({ id: _staff.id, ..._staff.data() })
  })

  return staffsData
}
